import React, { useState, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import moment from "moment";
import "./FileUploadModal.scss";
import cancel from "../../../assets/images/cancel.svg";
import enabledUpload from "../../../assets/images/enabled-upload.svg";
import { isFileValid } from "../../../Utils/FileValidation";
import DsbpCommonPopup from "../../DSBP/DsbpCommonPopup";
import PreUploadFileListTable from "./PreUploadFileListTable";
import {
  defaultAllowedFileType,
  fileTypeErrorMessage,
} from "../../../constants/FileUploadConstant";

const FileUploadModal = ({
  visible,
  onHide,
  onUpload,
  acceptType = defaultAllowedFileType,
  errorType = fileTypeErrorMessage,
}) => {
  const toast = useRef(null);
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    processFiles(event.dataTransfer.files);
  };

  const handleChange = (event) => {
    event.preventDefault();
    processFiles(event.target.files);
  };

  const processFiles = (files) => {
    const newFiles = Array.from(files);
    const validFiles = [];
    let hasError = false;

    const ownerName = localStorage.getItem("FirstName") + " " + localStorage.getItem("LastName");
    const ownerEmail = `${localStorage.getItem("userId")}@pg.com`;

    newFiles.forEach((file) => {
      const [isValidFile, errorMsg] = isFileValid(
        file,
        acceptType,
        undefined,
        acceptType,
        errorType,
      );

      if (isValidFile) {
        validFiles.push({
          File_Name: file.name,
          fileSize: file.size,
          fileType: file.type,
          file: file,
          Full_Name: ownerName,
          Email: ownerEmail,
          Timestamp: moment().utc().format("YYYYMMDD[T]HHmmss.SSS [GMT]"),
        });
      } else {
        setErrorMessage(errorMsg);
        hasError = true;
      }
    });

    if (!hasError) {
      setErrorMessage("");
      setIsValid(true);
      setFileList((prevList) => [...prevList, ...validFiles]);
    } else {
      setIsValid(false);
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const removeFile = (index) => {
    setFileList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "No Files",
        detail: "Please select at least one file to upload.",
        life: 3000,
      });
      return;
    }

    setIsUploading(true);
    try {
      // Call the onUpload callback with file list
      if (onUpload) {
        await onUpload(fileList);
      }

      // Clear file list and close modal
      setFileList([]);
      setErrorMessage("");
      onHide();
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    // Prevent cancel during upload
    if (isUploading) {
      return;
    }
    
    if (fileList.length > 0) {
      setShowCancelConfirmation(true);
    } else {
      setFileList([]);
      setErrorMessage("");
      onHide();
    }
  };

  const confirmCancel = () => {
    setShowCancelConfirmation(false);
    setFileList([]);
    setErrorMessage("");
    onHide();
  };

  const cancelCancelConfirmation = () => {
    setShowCancelConfirmation(false);
  };

  const onUploadButtonClick = () => {
    inputRef.current.click();
  };

  const dialogFooter = (
    <div className="footerbutton-flex">
      <button
        type="button"
        className="button-layout btn btn-secondary"
        onClick={handleCancel}
        disabled={isUploading}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="button-layout btn btn-primary"
        onClick={handleUpload}
        disabled={fileList.length === 0 || isUploading}
      >
        {isUploading ? "Uploading..." : "Upload"}
      </button>
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={handleCancel}
        header="Upload Files"
        modal
        className="file-upload-modal"
        footer={dialogFooter}
        breakpoints={{ "960px": "75vw", "640px": "90vw" }}
        closable={!isUploading}
      >
        <div className="modal-content">
          <div
            className={`drag-drop-area ${dragActive ? "drag-active" : ""}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={onUploadButtonClick}
            style={{ opacity: isUploading ? 0.5 : 1, pointerEvents: isUploading ? "none" : "auto" }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={acceptType}
              className="file-input"
              onChange={handleChange}
              disabled={isUploading}
            />
            <div className="drag-drop-content">
              <img src={enabledUpload} alt="Upload" className="upload-icon" />
              <p className="drag-drop-text">
                Drag and drop files here or Click to <span className="click-here">Browse</span>
              </p>
            </div>
          </div>

          {errorMessage && !isValid && (
            <div className="error-message-wrapper">
              <img src={cancel} alt="error icon" />
              <div className="error-message">{errorMessage}</div>
            </div>
          )}

          {fileList.length > 0 && (
            <PreUploadFileListTable fileList={fileList} onDelete={removeFile} />
          )}
        </div>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <DsbpCommonPopup
        dasbpDialog={showCancelConfirmation}
        setDasbpDialog={setShowCancelConfirmation}
        actionHeader="Cancel Upload"
        onSubmit={confirmCancel}
        onNoClick={cancelCancelConfirmation}
        yesButtonShow={false}
        submitButtonShow={true}
        deleteButtonShow={false}
        disconnectButtonShow={true}
      >
        <p>Are you sure you want to cancel the upload? Selected files will be cleared.</p>
      </DsbpCommonPopup>
    </>
  );
};

export default FileUploadModal;
